import React, { useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";

// Forgot Password Component
export const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    try {
      setMessage("");
      setError("");
      setLoading(true);

      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/users/forgot-password`, { email });

      setMessage(response.data.message);
    } catch (err) {
      setError(err.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="forgot-password bg-gradient-to-r from-black to-purple-900 flex justify-center items-center min-h-screen bg-gray-50 px-4 py-6">
      <div className="w-full max-w-md bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
          Forgot Password
        </h2>
        <form onSubmit={handleForgotPassword} className="space-y-6">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Email Address
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              className="block w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 shadow-sm text-base disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="Enter your email"
            />
          </div>
          <div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-indigo-600 text-white text-sm font-semibold py-3 rounded-lg hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending...
                </>
              ) : (
                "Submit"
              )}
            </button>
          </div>
        </form>
        {message && (
          <p className="text-sm text-green-600 mt-4 text-center">{message}</p>
        )}
        {error && <p className="text-sm text-red-600 mt-4 text-center">{error}</p>}
      </div>
    </div>
  );

};

// Reset Password Component
export const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Validation state
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [passwordLengthValid, setPasswordLengthValid] = useState(false);

  const handlePasswordChange = (e) => {
    const pwd = e.target.value;
    setNewPassword(pwd);
    setPasswordLengthValid(pwd.length >= 6);
    setPasswordsMatch(pwd === confirmPassword);
  };

  const handleConfirmPasswordChange = (e) => {
    const confirm = e.target.value;
    setConfirmPassword(confirm);
    setPasswordsMatch(newPassword === confirm);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    try {
      setMessage("");
      setError("");
      setLoading(true);

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/reset-password/${token}`,
        { newPassword }
      );

      setMessage(response.data.message);

      // Redirect to the iPhone app
      setTimeout(() => {
        // Try to open the app
        window.location.href = "uhyve://provider-dashboard/signup?initialMode=login";

        // Fallback or additional guidance
        // window.location.href = "https://apps.apple.com/ca/app/uhyve/id6751551335"; 
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="reset-password min-h-screen flex flex-col justify-center items-center bg-white px-4 md:px-6">
      <div className="w-full max-w-md bg-white p-2">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Reset Password
          </h2>
          <p className="text-gray-500 text-sm">
            Please create a new password for your account.
          </p>
        </div>

        <form onSubmit={handleResetPassword} className="space-y-6">
          <div className="space-y-4">
            <div>
              <label
                htmlFor="newPassword"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                New Password
              </label>
              <input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={handlePasswordChange}
                required
                disabled={loading}
                className="block w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-base outline-none"
                placeholder="Enter new password"
              />
              {!passwordLengthValid && newPassword.length > 0 && (
                <p className="text-xs text-red-500 mt-1 pl-1">
                  Must be at least 6 characters
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Confirm Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                required
                disabled={loading}
                className={`block w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-base outline-none ${!passwordsMatch && confirmPassword.length > 0 ? 'border-red-300 ring-2 ring-red-100' : ''
                  }`}
                placeholder="Confirm new password"
              />
              {!passwordsMatch && confirmPassword.length > 0 && (
                <p className="text-xs text-red-500 mt-1 pl-1">
                  Passwords do not match
                </p>
              )}
            </div>
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={!passwordLengthValid || !passwordsMatch || loading || !newPassword || !confirmPassword}
              className={`w-full text-white text-sm font-bold py-4 rounded-xl focus:outline-none focus:ring-4 focus:ring-purple-200 transition-all transform active:scale-95 shadow-lg
                ${passwordLengthValid && passwordsMatch && !loading && newPassword && confirmPassword
                  ? "bg-purple-600 hover:bg-purple-700 hover:shadow-purple-500/30"
                  : "bg-gray-300 cursor-not-allowed shadow-none"
                }`}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Resetting...</span>
                </div>
              ) : (
                "Reset Password"
              )}
            </button>
          </div>
        </form>

        {message && (
          <div className="mt-6 p-4 bg-green-50 border border-green-100 rounded-xl flex items-start gap-3 animate-pulse">
            <div className="text-green-500 mt-0.5">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-green-800">{message}</p>
              <p className="text-xs text-green-600 mt-1">Opening UHyve app...</p>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-6 p-4 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3">
            <div className="text-red-500 mt-0.5">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <p className="text-sm font-medium text-red-800">{error}</p>
          </div>
        )}

        <div className="mt-8 text-center">
          <a href="uhyve://" className="text-xs text-gray-400 hover:text-gray-600">Back to App</a>
        </div>
      </div>
    </div>
  );
};

